import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  Building2, Users, TrendingUp, DollarSign, Clock,
  MapPin, BarChart3, Wallet, Target, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

function StatCard({ icon: Icon, label, value, color = 'blue', delay = 0 }) {
  const colorMap = {
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-500/20 text-blue-400',
    lime: 'from-lime-500/20 to-lime-500/5 border-lime-400/20 text-lime-400',
    pink: 'from-pink-500/20 to-pink-500/5 border-pink-400/20 text-pink-400',
    orange: 'from-orange-500/20 to-orange-500/5 border-orange-400/20 text-orange-400',
  };
  return (
    <motion.div {...fadeUp} transition={{ delay: delay * 0.08 }}
      className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-4`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="opacity-70" />
        <span className="text-xs uppercase tracking-wider opacity-70 font-manrope">{label}</span>
      </div>
      <p className="font-barlow text-2xl font-bold text-white">{value}</p>
    </motion.div>
  );
}

export default function VenueDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/venue');
      setData(res.data);
    } catch (err) {
      console.error('Venue dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = data?.stats || {};
  const w = data?.wallet || {};
  const venues = data?.venues || [];

  const chartData = venues.map(v => ({
    name: v.name.length > 15 ? v.name.substring(0, 15) + '...' : v.name,
    utilization: v.utilization,
    sessions: v.sessions,
  }));

  return (
    <div data-testid="venue-dashboard" className="space-y-6">
      <motion.div {...fadeUp}>
        <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight flex items-center gap-2">
          <Building2 size={28} className="text-blue-400" />
          Venue Hub
        </h1>
        <p className="text-slate-400 text-sm font-manrope mt-0.5">Monitor venue utilization & revenue</p>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Building2} label="Total Sessions" value={s.total_sessions || 0} color="blue" delay={0} />
        <StatCard icon={Users} label="Total Bookings" value={s.total_bookings || 0} color="lime" delay={1} />
        <StatCard icon={TrendingUp} label="Utilization" value={`${s.utilization_rate || 0}%`} color="blue" delay={2} />
        <StatCard icon={DollarSign} label="Revenue" value={`${(s.total_revenue_php || 0).toLocaleString()} PHP`} color="lime" delay={3} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={BarChart3} label="Avg Fill Rate" value={`${s.avg_fill_rate || 0}%`} color="blue" delay={4} />
        <StatCard icon={Clock} label="Upcoming" value={s.upcoming_sessions || 0} color="lime" delay={5} />
        <StatCard icon={Wallet} label="Balance" value={`${w.balance || 0} $PLAY`} color="blue" delay={6} />
      </div>

      {/* Venue Utilization Chart */}
      {chartData.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.3 }}
          className="bg-slate-950/60 backdrop-blur-xl border border-white/10 rounded-xl p-5"
        >
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-4">
            Venue Utilization
          </h3>
          <div className="h-64" data-testid="venue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: '#f8fafc' }}
                />
                <Bar dataKey="utilization" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Utilization %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Venues List */}
      <motion.div {...fadeUp} transition={{ delay: 0.35 }}>
        <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
          <MapPin size={16} className="text-blue-400" />
          Venue Performance
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {venues.map((v, i) => (
            <div key={i}
              className="bg-slate-950/60 border border-white/10 rounded-xl p-4 hover:border-blue-500/20 transition-all"
              data-testid={`venue-card-${i}`}
            >
              <h4 className="text-white font-medium text-sm flex items-center gap-1.5 mb-2">
                <MapPin size={14} className="text-blue-400" />
                {v.name}
              </h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-400">Sessions</span>
                  <span className="text-white font-mono">{v.sessions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Utilization</span>
                  <span className={`font-mono ${v.utilization >= 70 ? 'text-lime-400' : v.utilization >= 40 ? 'text-blue-400' : 'text-pink-400'}`}>{v.utilization}%</span>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-800 rounded-full mt-3 overflow-hidden">
                <div className={`h-full rounded-full ${v.utilization >= 70 ? 'bg-lime-400' : v.utilization >= 40 ? 'bg-blue-500' : 'bg-pink-500'}`}
                  style={{ width: `${v.utilization}%` }} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Upcoming Sessions */}
      {data?.upcoming_sessions?.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.4 }}>
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3">
            Upcoming Sessions
          </h3>
          <div className="bg-slate-950/60 border border-white/10 rounded-xl overflow-hidden">
            {data.upcoming_sessions.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5">
                <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400 flex-shrink-0">
                  <Target size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{s.sport} — {s.venue_name}</p>
                  <p className="text-slate-500 text-xs">{new Date(s.start_time).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-white font-mono">{s.current_bookings}/{s.capacity}</span>
                  <p className="text-[10px] text-slate-500">{s.fill_rate}% full</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
